import React, { useEffect, useRef, useState } from "react";
import cs from "classnames";
import styles from "./styles.module.scss";
import { Tooltip } from "../../../components/Tooltip";
import {
  AlertIcon,
  ChevronDownIcon,
  CopyIcon,
  CreateOrderIcon,
  FillModeIcon,
  PasteIcon,
  PlusIcon,
  ShuffleIcon,
  SpinnerIcon,
  TrashIcon,
} from "./icons";
import { AppleIcon, GoogleIcon } from "../AppInsights/icons";
import {
  DirectIcon,
  KeywordsIcon,
  RatingsIcon,
  ReviewsIcon,
} from "../OrderTypeSelector/icons";
import { OrderTypeSelector } from "../OrderTypeSelector";
import useIsPhone from "../../../hooks/useIsPhone";
import {
  KEYWORD_LIST_SEPARATOR,
  OrderPlannerProps,
  PLANNER_FILL_MODES,
  PlannerBlockData,
  PlannerBlockKind,
  PlannerDay,
  PlannerFill,
  PlannerFillMode,
  PlannerRowData,
  PlannerStore,
} from "./types";

const STORE_ICON: Record<
  PlannerStore,
  React.FC<React.SVGProps<SVGSVGElement>>
> = {
  apple: AppleIcon,
  google: GoogleIcon,
};

const STORE_LABEL: Record<PlannerStore, string> = {
  apple: "App store",
  google: "Play store",
};

/** What each kind of block is called and drawn with in its head. */
const KIND: Record<
  PlannerBlockKind,
  {
    label: string;
    /** Head of the first column of the grid. */
    rowsTitle: string;
    Icon: React.FC<React.SVGProps<SVGSVGElement>>;
  }
> = {
  keywords: { label: "Keywords", rowsTitle: "Key", Icon: KeywordsIcon },
  direct: { label: "Direct", rowsTitle: "Installs", Icon: DirectIcon },
  ratings: { label: "Ratings", rowsTitle: "Rating", Icon: RatingsIcon },
  reviews: { label: "Reviews", rowsTitle: "", Icon: ReviewsIcon },
};

/** A row's name: the keyword typed, or the fixed label of the row. */
const rowName = (row: PlannerRowData) => row.label ?? row.keyword;

const StarGlyph: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    className={className}
    viewBox="0 0 16 16"
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M8 1.5l1.9 4 4.4.5-3.3 3 .9 4.4L8 11.2l-3.9 2.2.9-4.4-3.3-3 4.4-.5z" />
  </svg>
);

/** The fixed name of a row ("Direct", "5 stars"), where a keyword is typed. */
const RowLabel: React.FC<{ row: PlannerRowData; className?: string }> = ({
  row,
  className,
}) => (
  <span className={cs(styles.rowLabel, className)} title={row.label}>
    {row.stars && <StarGlyph className={styles.rowLabelStar} />}
    <span className={styles.rowLabelText}>{row.label}</span>
  </span>
);

/** App logo: URL, node, or the title's initial as a fallback tile. */
const AppLogo: React.FC<{ icon?: React.ReactNode; name: string }> = ({
  icon,
  name,
}) => {
  if (typeof icon === "string" && icon) {
    return <img className={styles.appIcon} src={icon} alt="" />;
  }
  if (icon) {
    return <span className={styles.appIcon}>{icon}</span>;
  }
  return (
    <span className={cs(styles.appIcon, styles.appIconFallback)}>
      {(name || "?").slice(0, 1).toUpperCase()}
    </span>
  );
};

const formatInt = (value: number) =>
  Number.isFinite(value) ? new Intl.NumberFormat("en-US").format(value) : "0";

/** Native select dressed as a DS control (chevron drawn on top of the field). */
const Select: React.FC<{
  value: string;
  options: { value: string; label: string }[];
  onChange?: (value: string) => void;
  ariaLabel: string;
  disabled?: boolean;
  className?: string;
}> = ({ value, options, onChange, ariaLabel, disabled, className }) => (
  <span className={cs(styles.select, className)}>
    <select
      className={styles.selectField}
      value={value}
      aria-label={ariaLabel}
      disabled={disabled}
      onChange={(event) => onChange?.(event.target.value)}
    >
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
    <ChevronDownIcon className={styles.selectChevron} />
  </span>
);

/**
 * Comment input with the DS tooltip holding the full note — shown only when
 * the column actually cuts the text (measured on hover) and not while the
 * manager is typing in it.
 */
const CommentInput: React.FC<{
  value: string;
  keyword: string;
  readOnly?: boolean;
  onChange: (value: string) => void;
}> = ({ value, keyword, readOnly, onChange }) => {
  const [truncated, setTruncated] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  return (
    <Tooltip
      title={truncated ? value : undefined}
      placement="top"
      appendToBody
      wrapperClassName={styles.commentAnchor}
      className={styles.commentTooltip}
    >
      <input
        ref={inputRef}
        className={cs(styles.input, styles.inputComment)}
        value={value}
        placeholder="Add comment"
        readOnly={readOnly}
        aria-label={`Comment for ${keyword || "keyword"}`}
        onMouseEnter={() => {
          const input = inputRef.current;
          setTruncated(
            !!input &&
              document.activeElement !== input &&
              input.scrollWidth > input.clientWidth,
          );
        }}
        onFocus={() => setTruncated(false)}
        onChange={(event) => onChange(event.target.value)}
      />
    </Tooltip>
  );
};

const EMPTY_FILL: PlannerFill = { mode: "fixed", value: "" };

/**
 * Fill cell: a mode and a number the host spreads over the row's days — per
 * day for Fixed, the row's total for the Random modes. A Random fill can be
 * drawn again without retyping it.
 *
 * The mode shows as a bar glyph of the shape it gives the days, so the column
 * stays narrow; the native select over it lists the full names.
 */
const FillCell: React.FC<{
  fill: PlannerFill;
  keyword: string;
  /** What the filled days count ("installs", "ratings"). */
  valueWord?: string;
  readOnly?: boolean;
  /** The row has no keyword yet — nothing to fill, the cell is off. */
  disabled?: boolean;
  onChange: (fill: PlannerFill) => void;
}> = ({
  fill,
  keyword,
  valueWord = "installs",
  readOnly,
  disabled,
  onChange,
}) => {
  const label = keyword || "keyword";
  const locked = readOnly || disabled;
  const canShuffle = !locked && fill.mode !== "fixed" && !!fill.value;
  const modeLabel =
    PLANNER_FILL_MODES.find((option) => option.value === fill.mode)?.label ??
    fill.mode;

  return (
    <div className={styles.fill}>
      <Tooltip
        title={modeLabel}
        placement="top"
        appendToBody
        compact
        wrapperClassName={styles.fillModeSlot}
      >
        <span
          className={cs(styles.fillMode, {
            [styles.fillModeLocked]: locked,
            [styles.fillModeDisabled]: disabled,
          })}
        >
          <FillModeIcon mode={fill.mode} className={styles.fillModeGlyph} />
          {!locked && <ChevronDownIcon className={styles.fillModeChevron} />}
          <select
            className={styles.fillModeField}
            value={fill.mode}
            disabled={locked}
            aria-label={`Fill mode for ${label}`}
            onChange={(event) =>
              onChange({
                ...fill,
                mode: event.target.value as PlannerFillMode,
              })
            }
          >
            {PLANNER_FILL_MODES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </span>
      </Tooltip>
      <input
        className={cs(styles.input, styles.inputNum, styles.inputFill)}
        value={fill.value}
        inputMode="numeric"
        placeholder={
          disabled ? undefined : fill.mode === "fixed" ? "/ day" : "total"
        }
        readOnly={readOnly}
        disabled={disabled}
        aria-label={`Fill ${valueWord} for ${label}`}
        onChange={(event) => onChange({ ...fill, value: event.target.value })}
      />
      {canShuffle && (
        <Tooltip
          title="Shuffle again"
          placement="top"
          appendToBody
          compact
          wrapperClassName={styles.fillShuffleSlot}
        >
          <button
            type="button"
            className={styles.rowAction}
            onClick={() => onChange(fill)}
            aria-label={`Shuffle installs of ${label}`}
          >
            <ShuffleIcon className={styles.rowActionIcon} />
          </button>
        </Tooltip>
      )}
    </div>
  );
};

type RowProps = {
  block: PlannerBlockData;
  row: PlannerRowData;
  days: PlannerDay[];
  /** The keyword's Volume and Rank columns (Keywords blocks only). */
  metrics: boolean;
  /** What a day cell counts, for its label ("Installs", "Ratings"). */
  valueLabel: string;
  readOnly?: boolean;
  /** This row's installs are the copied ones. */
  copied: boolean;
  canCopy: boolean;
  canPaste: boolean;
  keywordRef: React.RefCallback<HTMLInputElement>;
  onKeywordEnter: () => void;
  onRowRemove: OrderPlannerProps["onRowRemove"];
  onRowCopy: OrderPlannerProps["onRowCopy"];
  onRowPaste: OrderPlannerProps["onRowPaste"];
  onRowFieldChange: OrderPlannerProps["onRowFieldChange"];
  onRowKeywordCommit: OrderPlannerProps["onRowKeywordCommit"];
  onRowKeywordsPaste: OrderPlannerProps["onRowKeywordsPaste"];
  onCellChange: OrderPlannerProps["onCellChange"];
  onRowFillChange: OrderPlannerProps["onRowFillChange"];
};

/**
 * The keyword field of a row, in the table cell or on a phone's card: typing,
 * the commit on blur or Enter, and a pasted or comma-typed list turning into
 * rows.
 */
const KeywordInput: React.FC<
  Pick<
    RowProps,
    | "block"
    | "row"
    | "readOnly"
    | "keywordRef"
    | "onKeywordEnter"
    | "onRowFieldChange"
    | "onRowKeywordCommit"
    | "onRowKeywordsPaste"
  > & { className?: string }
> = ({
  block,
  row,
  readOnly,
  keywordRef,
  onKeywordEnter,
  onRowFieldChange,
  onRowKeywordCommit,
  onRowKeywordsPaste,
  className,
}) => {
  /**
   * Commits the typed keyword. Text typed with commas is a list, the same as a
   * pasted one — it goes to the host to become rows. True means Enter stays on
   * this row: the list landed below it, or the host turned the keyword down.
   */
  const commitKeyword = (value: string): boolean => {
    if (onRowKeywordsPaste && KEYWORD_LIST_SEPARATOR.test(value)) {
      onRowKeywordsPaste(block.id, row.id, value, true);
      return true;
    }
    return onRowKeywordCommit?.(block.id, row.id, value) === false;
  };

  return (
    <input
      ref={keywordRef}
      className={cs(
        styles.input,
        styles.inputKeyword,
        { [styles.inputDuplicate]: row.duplicate },
        className,
      )}
      value={row.keyword}
      placeholder="keyword"
      readOnly={readOnly}
      aria-invalid={row.duplicate || undefined}
      title={
        row.duplicate
          ? `"${row.keyword.trim()}" is already in this block`
          : row.keyword || undefined
      }
      onChange={(event) =>
        onRowFieldChange?.(block.id, row.id, "keyword", event.target.value)
      }
      onBlur={(event) => commitKeyword(event.target.value)}
      onKeyDown={(event) => {
        // Enter while an IME is composing picks the candidate, not the row.
        if (event.key !== "Enter" || event.nativeEvent.isComposing) return;
        // A list just became rows below this one, or the keyword didn't
        // stay — either way, stay on it.
        const stay = commitKeyword((event.target as HTMLInputElement).value);
        if (!readOnly && !stay) onKeywordEnter();
      }}
      onPaste={(event) => {
        if (readOnly || !onRowKeywordsPaste) return;
        const text = event.clipboardData.getData("text");
        // One keyword (a sheet cell brings a trailing line break) pastes
        // natively; a list becomes rows.
        if (!KEYWORD_LIST_SEPARATOR.test(text.trim())) return;
        event.preventDefault();
        const input = event.currentTarget;
        const replace =
          !input.value.trim() ||
          (input.selectionStart === 0 &&
            input.selectionEnd === input.value.length);
        onRowKeywordsPaste(block.id, row.id, text, replace);
      }}
    />
  );
};

/** Remove, Copy installs and Paste installs of a row. */
const RowActions: React.FC<
  Pick<
    RowProps,
    | "block"
    | "row"
    | "readOnly"
    | "copied"
    | "canCopy"
    | "canPaste"
    | "onRowRemove"
    | "onRowCopy"
    | "onRowPaste"
    | "valueLabel"
  > & { className?: string }
> = ({
  block,
  row,
  readOnly,
  copied,
  canCopy,
  canPaste,
  onRowRemove,
  onRowCopy,
  onRowPaste,
  valueLabel,
  className,
}) => {
  const valueWord = valueLabel.toLowerCase();
  return (
    <div className={cs(styles.rowActions, className)}>
      {/* Right-aligned, and the buttons that stay up (Paste, a lit Copy)
      come last: the hover-only ones open to their left, so nothing
      slides under the cursor — a Paste click can't land on Remove. */}
      {/* A fixed row belongs to its block: it goes with the block. */}
      {!readOnly && !row.label && (
        <button
          type="button"
          className={cs(styles.rowRemove, styles.rowActionOnHover)}
          onClick={() => onRowRemove?.(block.id, row.id)}
          aria-label={`Remove ${row.keyword || "keyword"}`}
        >
          <TrashIcon className={styles.rowRemoveIcon} />
        </button>
      )}
      {canCopy && (
        <Tooltip
          title={
            copied ? "Copied — click or Esc to cancel" : `Copy ${valueWord}`
          }
          placement="top"
          appendToBody
          compact
          wrapperClassName={cs(styles.rowActionSlot, {
            [styles.rowActionOnHover]: !copied,
          })}
        >
          <button
            type="button"
            className={cs(styles.rowAction, {
              [styles.rowActionActive]: copied,
            })}
            onClick={() => onRowCopy?.(block.id, row.id)}
            aria-pressed={copied}
            aria-label={`Copy ${valueWord} of ${rowName(row) || "keyword"}`}
          >
            <CopyIcon className={styles.rowActionIcon} />
          </button>
        </Tooltip>
      )}
      {canPaste && (
        <Tooltip
          title={`Paste ${valueWord}`}
          placement="top"
          appendToBody
          compact
          wrapperClassName={styles.rowActionSlot}
        >
          <button
            type="button"
            className={cs(styles.rowAction, styles.rowPaste)}
            onClick={() => onRowPaste?.(block.id, row.id)}
            aria-label={`Paste ${valueWord} into ${rowName(row) || "keyword"}`}
          >
            <PasteIcon className={styles.rowActionIcon} />
          </button>
        </Tooltip>
      )}
    </div>
  );
};

const PlannerRow: React.FC<RowProps> = (props) => {
  const {
    block,
    row,
    days,
    metrics,
    valueLabel,
    readOnly,
    copied,
    canPaste,
    onRowFieldChange,
    onCellChange,
    onRowFillChange,
  } = props;

  // Installs are planned for a keyword: until the row has one, its Fill and
  // day cells stay off. A fixed row is named from the start.
  const noKeyword = !row.label && !row.keyword.trim();
  const noKeywordHint = noKeyword ? "Enter a keyword first" : undefined;
  const name = rowName(row);

  return (
    <tr
      className={cs(styles.row, {
        [styles.rowCopied]: copied,
        [styles.rowPasteTarget]: canPaste,
      })}
    >
      <td className={cs(styles.cell, styles.cellKeyword)}>
        {row.label ? <RowLabel row={row} /> : <KeywordInput {...props} />}
        <RowActions {...props} />
      </td>

      {/* Volume and the rank the keyword holds before the order — pulled from
        the app's tracked keywords, never typed. */}
      {metrics && (
        <>
          <td className={cs(styles.cell, styles.cellAsa)}>
            <span className={styles.metricValue}>{row.asa || "-"}</span>
          </td>

          <td className={cs(styles.cell, styles.cellPosition)}>
            <span className={cs(styles.metricValue, styles.metricRank)}>
              {row.positionTo || "-"}
            </span>
          </td>
        </>
      )}

      <td className={cs(styles.cell, styles.cellComment)}>
        <CommentInput
          value={row.comment ?? ""}
          keyword={name}
          readOnly={readOnly}
          onChange={(value) =>
            onRowFieldChange?.(block.id, row.id, "comment", value)
          }
        />
      </td>

      <td className={cs(styles.cell, styles.cellFill)} title={noKeywordHint}>
        <FillCell
          fill={row.fill ?? EMPTY_FILL}
          keyword={name}
          valueWord={valueLabel.toLowerCase()}
          readOnly={readOnly}
          disabled={noKeyword}
          onChange={(fill) => onRowFillChange?.(block.id, row.id, fill)}
        />
      </td>

      {days.map((day) => (
        <td
          key={day.key}
          className={cs(styles.cell, styles.cellDay, {
            [styles.cellWeekend]: day.weekend,
          })}
          title={noKeywordHint}
        >
          <input
            className={cs(styles.input, styles.inputNum)}
            value={row.values[day.key] ?? ""}
            inputMode="numeric"
            readOnly={readOnly}
            disabled={noKeyword}
            aria-label={`${valueLabel} for ${name || "keyword"} on ${day.label}`}
            onChange={(event) =>
              onCellChange?.(block.id, row.id, day.key, event.target.value)
            }
          />
        </td>
      ))}

      <td className={cs(styles.cell, styles.cellTotal)}>
        <span className={styles.totalValue}>{formatInt(row.total)}</span>
      </td>
    </tr>
  );
};

/**
 * A keyword on a phone, where the sheet's row would run off the screen: the
 * keyword and its actions, the figures, the comment, the Fill that does most
 * of the typing, then the days as a grid of labelled inputs.
 */
const PlannerCard: React.FC<RowProps> = (props) => {
  const {
    block,
    row,
    days,
    metrics,
    valueLabel,
    readOnly,
    copied,
    canPaste,
    onRowFieldChange,
    onCellChange,
    onRowFillChange,
  } = props;
  const noKeyword = !row.label && !row.keyword.trim();
  const name = rowName(row);

  return (
    <div
      className={cs(styles.card, {
        [styles.cardCopied]: copied,
      })}
      role="listitem"
    >
      <div className={styles.cardHead}>
        {row.label ? (
          <RowLabel row={row} className={styles.cardKeyword} />
        ) : (
          <KeywordInput {...props} className={styles.cardKeyword} />
        )}
        <RowActions
          {...props}
          className={cs(styles.cardActions, {
            [styles.cardActionsPaste]: canPaste,
          })}
        />
      </div>

      <div className={styles.cardMetrics}>
        {metrics && (
          <>
            <span className={styles.cardMetric}>
              <span className={styles.cardMetricLabel}>ASA</span>
              {row.asa || "-"}
            </span>
            <span className={styles.cardMetric}>
              <span className={styles.cardMetricLabel}>Rank</span>
              <span className={styles.metricRank}>{row.positionTo || "-"}</span>
            </span>
          </>
        )}
        <span className={cs(styles.cardMetric, styles.cardTotal)}>
          <span className={styles.cardMetricLabel}>Total</span>
          {formatInt(row.total)}
        </span>
      </div>

      <CommentInput
        value={row.comment ?? ""}
        keyword={name}
        readOnly={readOnly}
        onChange={(value) =>
          onRowFieldChange?.(block.id, row.id, "comment", value)
        }
      />

      <div className={styles.cardFill}>
        <span className={styles.cardMetricLabel}>Fill</span>
        <FillCell
          fill={row.fill ?? EMPTY_FILL}
          keyword={name}
          valueWord={valueLabel.toLowerCase()}
          readOnly={readOnly}
          disabled={noKeyword}
          onChange={(fill) => onRowFillChange?.(block.id, row.id, fill)}
        />
      </div>

      {noKeyword ? (
        <p className={styles.cardHint}>Enter a keyword to plan its days.</p>
      ) : (
        <div className={styles.cardDays}>
          {days.map((day) => (
            <label
              key={day.key}
              className={cs(styles.cardDay, {
                [styles.cardDayWeekend]: day.weekend,
              })}
            >
              <span className={styles.cardDayLabel}>{day.label}</span>
              <input
                className={cs(
                  styles.input,
                  styles.inputNum,
                  styles.cardDayInput,
                )}
                value={row.values[day.key] ?? ""}
                inputMode="numeric"
                placeholder="0"
                readOnly={readOnly}
                aria-label={`${valueLabel} for ${name} on ${day.label}`}
                onChange={(event) =>
                  onCellChange?.(block.id, row.id, day.key, event.target.value)
                }
              />
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * Keyword Planner — the in-app version of the wide media-plan sheet managers
 * fill in by hand: an app header (name + store link), then one geo block per
 * country where keywords are rows and dates are columns. Every block closes
 * with its own "Create order" button, so a plan reaches the keywords order
 * flow block by block.
 *
 * Pure presentational: totals, validation and the create-order handoff live in
 * the host page; this component only renders values and reports edits.
 */
export const OrderPlanner: React.FC<OrderPlannerProps> = ({
  appName,
  appIcon,
  appDeveloper,
  appId,
  appUrl,
  store,
  startDate,
  endDate,
  days: sharedDays = [],
  blocks,
  countryOptions,
  maxKeywords = 100,
  maxDays = 31,
  headerSlot,
  actionsSlot,
  appSlot,
  storeLinkSlot,
  startDateSlot,
  endDateSlot,
  renderCountry,
  renderDates,
  renderOrderType,
  readOnly,
  copiedRowId,
  canPasteRow,
  onAppClick,
  onViewInStore,
  onStartDateChange,
  onEndDateChange,
  onBlockAdd,
  blockKinds,
  onBlockKindChange,
  onBlockRemove,
  onBlockCountryChange,
  onCreateOrder,
  onRowAdd,
  onRowRemove,
  onRowCopy,
  onRowPaste,
  onRowFieldChange,
  onRowKeywordCommit,
  onRowKeywordsPaste,
  onCellChange,
  onRowFillChange,
  className,
}) => {
  // A phone has no room for the sheet: each keyword is a card.
  const isPhone = useIsPhone();
  // A block can order more than one thing: it carries the type cards of the
  // new-order page, its type checked, and switches in place.
  const multiKind = (blockKinds?.length ?? 0) > 1;
  // Each block on its own dates: the header has no shared range to show.
  const perBlockDates = !!renderDates;
  const daysOverflow = !perBlockDates && sharedDays.length > maxDays;

  // Enter in a keyword goes down a row the way a sheet does; past the last
  // filled row it starts a new one. The host adds that row, so its input is
  // focused on the render that brings it in.
  const keywordInputs = useRef(new Map<string, HTMLInputElement>());
  const pendingFocus = useRef<{ blockId: string; index: number } | null>(null);

  useEffect(() => {
    const request = pendingFocus.current;
    if (!request) return;
    const row = blocks.find((block) => block.id === request.blockId)?.rows[
      request.index
    ];
    const input = row && keywordInputs.current.get(row.id);
    if (!input) return;
    pendingFocus.current = null;
    input.focus();
  });

  const keywordRef =
    (rowId: string): React.RefCallback<HTMLInputElement> =>
    (node) => {
      if (node) keywordInputs.current.set(rowId, node);
      else keywordInputs.current.delete(rowId);
    };

  const handleKeywordEnter = (block: PlannerBlockData, index: number) => {
    const next = block.rows[index + 1];
    if (next) {
      keywordInputs.current.get(next.id)?.focus();
      return;
    }
    // An empty last row is already the one to type into.
    if (!block.rows[index]?.keyword.trim() || !onRowAdd) return;
    pendingFocus.current = { blockId: block.id, index: index + 1 };
    onRowAdd(block.id);
  };

  return (
    <section className={cs(styles.planner, className)}>
      {headerSlot}

      <div className={styles.head}>
        <div className={styles.headApp}>
          <AppLogo icon={appIcon} name={appName} />

          <div className={styles.appInfo}>
            {appSlot ?? (
              <button
                type="button"
                className={styles.appTrigger}
                onClick={onAppClick}
                disabled={readOnly}
              >
                <span
                  className={cs(styles.appName, {
                    [styles.appNamePlaceholder]: !appName,
                  })}
                >
                  {appName || "Select app"}
                </span>
                <ChevronDownIcon className={styles.appChevron} />
              </button>
            )}

            {(appDeveloper || appId != null) && (
              <div className={styles.appMeta}>
                {appDeveloper && <span>{appDeveloper}</span>}
                {appDeveloper && appId != null && (
                  <span className={styles.appMetaDot}>•</span>
                )}
                {appId != null && <span>ID {appId}</span>}
              </div>
            )}

            {storeLinkSlot ??
              (appUrl && store && (
                <a
                  className={styles.storeLink}
                  href={appUrl}
                  target="_blank"
                  rel="noreferrer"
                  onClick={(e) => {
                    // Opened the way App Insights opens it: from script, not the browser's own link handling.
                    e.preventDefault();
                    window.open(appUrl, "_blank", "noopener,noreferrer");
                    onViewInStore?.();
                  }}
                >
                  <span className={styles.storeIconBox}>
                    {React.createElement(STORE_ICON[store], {
                      className: styles.storeIcon,
                    })}
                  </span>
                  <span className={styles.storeLinkText}>
                    View in {STORE_LABEL[store]}
                  </span>
                </a>
              ))}
          </div>
        </div>

        {!perBlockDates && (
          <div className={styles.headDates}>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>Start</span>
              {startDateSlot ?? (
                <input
                  type="date"
                  className={cs(styles.fieldInput, styles.fieldInputDate)}
                  value={startDate}
                  readOnly={readOnly}
                  aria-label="Start date"
                  onChange={(event) => onStartDateChange?.(event.target.value)}
                />
              )}
            </div>
            <div className={styles.field}>
              <span className={styles.fieldLabel}>End</span>
              {endDateSlot ?? (
                <input
                  type="date"
                  className={cs(styles.fieldInput, styles.fieldInputDate)}
                  value={endDate}
                  readOnly={readOnly}
                  aria-label="End date"
                  onChange={(event) => onEndDateChange?.(event.target.value)}
                />
              )}
            </div>
            <span
              className={cs(styles.daysChip, {
                [styles.daysChipError]: daysOverflow,
              })}
            >
              {sharedDays.length} day{sharedDays.length === 1 ? "" : "s"}
            </span>
          </div>
        )}

        {actionsSlot && <div className={styles.headActions}>{actionsSlot}</div>}
      </div>

      {daysOverflow && (
        <p className={styles.headWarning}>
          <AlertIcon className={styles.headWarningIcon} />
          An order covers at most {maxDays} days — shorten the range before
          creating orders.
        </p>
      )}

      <div className={styles.blocks}>
        {blocks.map((block) => {
          const countryLabel =
            block.countryLabel ||
            countryOptions.find((option) => option.value === block.country)
              ?.label ||
            block.country ||
            // A block whose geo is still unset ("Add geo block").
            "this geo";
          const kind = block.kind ?? "keywords";
          const isKeywords = kind === "keywords";
          const kindInfo = KIND[kind];
          const valueLabel = block.valueLabel ?? "Installs";
          const rowsOverflow = isKeywords && block.rows.length > maxKeywords;
          const days = block.days ?? sharedDays;
          // An ordered block is history — it stops taking edits.
          const blockLocked = readOnly || block.locked;
          const rowProps = (row: PlannerRowData, index: number): RowProps => ({
            block,
            row,
            days,
            metrics: isKeywords,
            valueLabel,
            readOnly: blockLocked,
            copied: row.id === copiedRowId,
            // An ordered block can lend its plan, not take one.
            canCopy: !readOnly && !!onRowCopy,
            canPaste:
              !!canPasteRow &&
              !blockLocked &&
              !!rowName(row).trim() &&
              row.id !== copiedRowId,
            keywordRef: keywordRef(row.id),
            onKeywordEnter: () => handleKeywordEnter(block, index),
            onRowRemove,
            onRowCopy,
            onRowPaste,
            onRowFieldChange,
            onRowKeywordCommit,
            onRowKeywordsPaste,
            onCellChange,
            onRowFillChange,
          });

          return (
            <article key={block.id} className={styles.block}>
              {multiKind && !blockLocked && (
                <OrderTypeSelector
                  name={`planner-block-kind-${block.id}`}
                  ariaLabel={`Order type of the ${countryLabel} block`}
                  value={kind}
                  options={blockKinds}
                  onChange={(next) => onBlockKindChange?.(block.id, next)}
                />
              )}
              <div className={styles.blockHead}>
                <span className={styles.blockCountry}>
                  {blockLocked ? (
                    <>
                      {block.countryIconUrl && (
                        <img
                          className={styles.blockFlag}
                          src={block.countryIconUrl}
                          alt=""
                        />
                      )}
                      <span className={styles.blockCode}>
                        {block.country.toUpperCase()}
                      </span>
                      <span className={styles.blockCountryName}>
                        {countryLabel}
                      </span>
                    </>
                  ) : (
                    (renderCountry?.(block) ?? (
                      <Select
                        className={styles.blockCountrySelect}
                        value={block.country}
                        options={countryOptions}
                        ariaLabel="Country of the geo block"
                        onChange={(value) =>
                          onBlockCountryChange?.(block.id, value)
                        }
                      />
                    ))
                  )}
                </span>

                {/* An ordered block keeps its type: named, no longer picked. */}
                {multiKind && blockLocked && (
                  <span className={styles.blockKind}>
                    <kindInfo.Icon
                      className={styles.blockKindIcon}
                      aria-hidden
                    />
                    {kindInfo.label}
                  </span>
                )}

                {perBlockDates && (
                  <span className={styles.blockDates}>
                    {renderDates?.(block)}
                    {/* A block with its own body has no day columns to count. */}
                    {!block.body && (
                      <span
                        className={cs(styles.daysChip, {
                          [styles.daysChipError]: days.length > maxDays,
                        })}
                      >
                        {days.length} day{days.length === 1 ? "" : "s"}
                      </span>
                    )}
                  </span>
                )}

                {/* The figures and Remove stay together: when the head wraps
                    they go to the next line as one. */}
                <span className={styles.blockHeadEnd}>
                  <span className={styles.blockSummary}>
                    {block.summary ?? (
                      <>
                        {block.rows.length} keyword
                        {block.rows.length === 1 ? "" : "s"} ·{" "}
                        {formatInt(block.total)} installs
                        {block.budget ? ` · ${block.budget}` : ""}
                      </>
                    )}
                  </span>

                  {!readOnly && (
                    <button
                      type="button"
                      className={styles.blockRemove}
                      onClick={() => onBlockRemove?.(block.id)}
                      aria-label={`Remove ${countryLabel} ${isKeywords ? "" : `${kindInfo.label.toLowerCase()} `}block`}
                    >
                      <TrashIcon className={styles.blockRemoveIcon} />
                    </button>
                  )}
                </span>
              </div>

              {block.body ? (
                block.body
              ) : isPhone ? (
                <div className={styles.cards} role="list">
                  {block.rows.map((row, index) => (
                    <PlannerCard key={row.id} {...rowProps(row, index)} />
                  ))}
                </div>
              ) : (
                <div className={styles.tableScroll}>
                  <table className={styles.table}>
                    <thead>
                      <tr className={styles.headerRow}>
                        <th
                          className={cs(
                            styles.headerCell,
                            styles.cellKeyword,
                            styles.headerCellKeyword,
                          )}
                        >
                          {kindInfo.rowsTitle}
                        </th>
                        {isKeywords && (
                          <>
                            <th
                              className={cs(styles.headerCell, styles.cellAsa)}
                              title="Search volume of the keyword"
                            >
                              ASA
                            </th>
                            <th
                              className={cs(
                                styles.headerCell,
                                styles.cellPosition,
                              )}
                              title="Current rank of the keyword in the store"
                            >
                              Rank
                            </th>
                          </>
                        )}
                        <th
                          className={cs(
                            styles.headerCell,
                            styles.cellComment,
                            styles.headerCellComment,
                          )}
                        >
                          Comment
                        </th>
                        <th
                          className={cs(
                            styles.headerCell,
                            styles.cellFill,
                            styles.headerCellFill,
                          )}
                          title="Fills the row's days: Fixed puts the number on every day, the Random modes split it as the total"
                        >
                          Fill
                        </th>
                        {days.map((day) => (
                          <th
                            key={day.key}
                            className={cs(styles.headerCell, styles.cellDay, {
                              [styles.cellWeekend]: day.weekend,
                            })}
                          >
                            {day.label}
                          </th>
                        ))}
                        <th className={cs(styles.headerCell, styles.cellTotal)}>
                          Total
                        </th>
                      </tr>
                    </thead>

                    <tbody>
                      {block.rows.map((row, index) => (
                        <PlannerRow key={row.id} {...rowProps(row, index)} />
                      ))}
                    </tbody>

                    {/* Direct has one row: a total of it would repeat it. */}
                    {kind !== "direct" && (
                      <tfoot>
                        <tr className={styles.totalsRow}>
                          <td
                            className={cs(
                              styles.cell,
                              styles.cellKeyword,
                              styles.totalsLabel,
                            )}
                          >
                            Total
                          </td>
                          {isKeywords && (
                            <>
                              <td className={cs(styles.cell, styles.cellAsa)} />
                              <td
                                className={cs(styles.cell, styles.cellPosition)}
                              />
                            </>
                          )}
                          <td className={cs(styles.cell, styles.cellComment)} />
                          <td className={cs(styles.cell, styles.cellFill)} />
                          {days.map((day) => (
                            <td
                              key={day.key}
                              className={cs(styles.cell, styles.cellDay, {
                                [styles.cellWeekend]: day.weekend,
                              })}
                            >
                              <span className={styles.totalValue}>
                                {formatInt(block.dayTotals?.[day.key] ?? 0)}
                              </span>
                            </td>
                          ))}
                          <td className={cs(styles.cell, styles.cellTotal)}>
                            <span
                              className={cs(
                                styles.totalValue,
                                styles.totalStrong,
                              )}
                            >
                              {formatInt(block.total)}
                            </span>
                          </td>
                        </tr>
                      </tfoot>
                    )}
                  </table>
                </div>
              )}

              {isKeywords && !block.body && !blockLocked && (
                <button
                  type="button"
                  className={styles.addRow}
                  onClick={() => onRowAdd?.(block.id)}
                >
                  <PlusIcon className={styles.addRowIcon} />
                  Add keyword
                </button>
              )}
              {block.extra}

              {!blockLocked && (block.error || rowsOverflow) && (
                <p className={styles.blockError}>
                  <AlertIcon className={styles.blockErrorIcon} />
                  {block.error ??
                    `An order takes at most ${maxKeywords} keywords — this block has ${block.rows.length}.`}
                </p>
              )}

              <div className={styles.blockFoot}>
                {block.notice ?? (
                  <>
                    <span className={styles.footHint}>
                      {block.orderTypeLabel ?? "Keywords"} order for{" "}
                      {countryLabel}
                    </span>
                    {renderOrderType?.(block)}
                    <button
                      type="button"
                      className={styles.createButton}
                      disabled={
                        readOnly ||
                        block.busy ||
                        !!block.error ||
                        block.createDisabled
                      }
                      onClick={() => onCreateOrder?.(block.id)}
                    >
                      {block.busy ? (
                        <SpinnerIcon
                          className={cs(
                            styles.createIcon,
                            styles.createSpinner,
                          )}
                        />
                      ) : (
                        <CreateOrderIcon className={styles.createIcon} />
                      )}
                      Create order
                    </button>
                  </>
                )}
              </div>
            </article>
          );
        })}
      </div>

      {!readOnly && (
        <button
          type="button"
          className={styles.addBlock}
          onClick={() => onBlockAdd?.()}
        >
          <PlusIcon className={styles.addBlockIcon} />
          Add geo block
        </button>
      )}
    </section>
  );
};

export default OrderPlanner;
